import { useMutation } from '@apollo/client/react'

import { graphql } from '~publish/graphql'

const RegisterAPIClientMutation = graphql(/* GraphQL */ `
  mutation registerAPIClient($input: RegisterAPIClientInput!) {
    registerAPIClient(input: $input) {
      ... on RegisterAPIClientSuccess {
        client {
          id
          clientId
          clientName
          clientSecret
          createdAt
          redirectUris
          privacyPolicyUri
          logoUri
          isSuspended
          scopes {
            name
          }
        }
      }
      ... on InvalidRedirectUrisError {
        message
      }
      ... on LimitReachedError {
        message
      }
      ... on NotFoundError {
        message
      }
    }
  }
`)

export type RegisterAPIClientInput = {
  clientName: string
  redirectUris: string[]
  scopes?: string[]
  privacyPolicyUri?: string
  logoUri?: string | null
  isPublicClient?: boolean
}

type RegisterAPIClientResult =
  | { success: true; client: { clientId: string } }
  | { success: false; error: string }

type UseRegisterAPIClientResult = {
  registerClient: (
    input: RegisterAPIClientInput,
  ) => Promise<RegisterAPIClientResult>
  isLoading: boolean
}

export const useRegisterAPIClient = (): UseRegisterAPIClientResult => {
  const [mutate, { loading }] = useMutation(RegisterAPIClientMutation)

  const registerClient = async (
    input: RegisterAPIClientInput,
  ): Promise<RegisterAPIClientResult> => {
    try {
      const result = await mutate({
        variables: { input },
      })

      if (result.error) {
        return { success: false, error: result.error.message }
      }

      const response = result.data?.registerAPIClient

      if (response && 'client' in response) {
        return { success: true, client: response.client }
      }

      if (response && 'message' in response) {
        return { success: false, error: response.message }
      }

      return {
        success: false,
        error: 'Failed to register client. Please try again.',
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred. Please try again.'

      return { success: false, error: message }
    }
  }

  return {
    registerClient,
    isLoading: loading,
  }
}
